import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Skeleton } from 'antd'
import dayjs from 'dayjs'
import { HStack, Icon, VStack } from 'govwell-ui'
import { CreateLetterheadInput } from 'types/graphql'

import { navigate, routes, useBlocker, useParams } from '@cedarjs/router'

import { EllipsisButton } from 'src/components/EllipsisButton'
import Page from 'src/components/shared/Page/Page'
import {
  useDeleteLetterheadMutation,
  useGetLetterheadByUuidQuery,
  useUpdateLetterheadMutation,
} from 'src/fetch/letterheads'
import { useSetRecordSettingsMutation } from 'src/fetch/recordSettings'
import { T } from 'src/govwell-ui'
import { ActionItem } from 'src/govwell-ui/types'
import useDebounce from 'src/hooks/use-debounce'
import useDisclosure from 'src/hooks/use-disclosure'
import { deepEqual } from 'src/utils'
import { DateFormats, formatDate } from 'src/utils/date'

import DeleteLetterheadModal from './DeleteLetterheadModal'
import LetterheadForm from './LetterheadForm'
import LetterheadPreview from './LetterheadPreview'
import { getInitialFormValues, LetterheadFormValues } from './types'

// Convert form values to the protocol (update input)
const formValuesToProtocol = (values: LetterheadFormValues): CreateLetterheadInput => {
  // Determine center value - only send fileId if there's actually a file
  const center =
    values.centerType === 'File' && values.centerFile?.id
      ? { fileId: values.centerFile.id }
      : { text: values.centerText }

  return {
    backgroundColor: values.backgroundColor,
    center,
    centerFileWidthPx: values.centerFileWidthPx,
    leftText: values.leftText,
    name: values.name,
    rightText: values.rightText,
  }
}

const LetterheadPage = () => {
  const { uuid } = useParams()
  const { data, isLoading, refetch } = useGetLetterheadByUuidQuery(uuid)
  const letterhead = useMemo(() => data?.getLetterheadByUuid, [data])

  const { mutateAsync: updateLetterhead, isPending: isSaving } = useUpdateLetterheadMutation()
  const { mutateAsync: deleteLetterhead } = useDeleteLetterheadMutation()
  const { mutateAsync: updateRecordSettings } = useSetRecordSettingsMutation()

  const deleteModalState = useDisclosure()

  // Track last updated time for display
  const [lastUpdatedAt, setLastUpdatedAt] = useState<string>()
  useEffect(() => {
    if (letterhead?.updatedAt) {
      setLastUpdatedAt(letterhead.updatedAt)
    }
  }, [letterhead?.updatedAt])

  const lastSavedString = useMemo(() => {
    const now = dayjs()
    if (!lastUpdatedAt) {
      return 'Not yet saved'
    }
    if (now.isSame(lastUpdatedAt, 'day')) {
      return `Last saved ${formatDate(lastUpdatedAt, DateFormats.Time)}`
    }
    return `Last saved ${formatDate(lastUpdatedAt, DateFormats.MonthNameDateNoTime)}`
  }, [lastUpdatedAt])

  // Track form values for live preview - only set after letterhead loads
  const [formValues, setFormValues] = useState<LetterheadFormValues | null>(null)

  // Track last saved protocol
  const [lastSavedProtocol, setLastSavedProtocol] = useState<CreateLetterheadInput | null>(null)
  const lastSavedProtocolRef = useRef<CreateLetterheadInput | null>(null)

  // Initialize form values and last saved protocol when letterhead loads
  useEffect(() => {
    if (letterhead) {
      const values = getInitialFormValues(letterhead)
      setFormValues(values)
      setLastSavedProtocol(formValuesToProtocol(values))
    }
  }, [letterhead])

  // Track the protocol (update input) derived from form values - only when formValues exists
  const protocol = useMemo(
    () => (formValues ? formValuesToProtocol(formValues) : null),
    [formValues]
  )
  const protocolRef = useRef<CreateLetterheadInput | null>(null)
  useEffect(() => {
    protocolRef.current = protocol
  }, [protocol])

  useEffect(() => {
    lastSavedProtocolRef.current = lastSavedProtocol
  }, [lastSavedProtocol])

  const handleFormValuesChange = useCallback((values: LetterheadFormValues) => {
    setFormValues(values)
  }, [])

  // Debounce protocol for autosave - only starts after protocol is defined
  const debouncedProtocol = useDebounce(protocol, 2000)

  const saveChanges = useCallback(
    async (input: CreateLetterheadInput) => {
      if (!letterhead?.id) {
        return
      }

      await updateLetterhead({
        id: letterhead.id,
        input,
      })
      setLastSavedProtocol(input)
      setLastUpdatedAt(new Date().toISOString())
    },
    [letterhead?.id, updateLetterhead]
  )

  // Autosave when debounced protocol differs from last saved
  useEffect(() => {
    if (
      !lastSavedProtocol ||
      !debouncedProtocol ||
      deepEqual(lastSavedProtocol, debouncedProtocol)
    ) {
      return
    }
    void saveChanges(debouncedProtocol)
  }, [debouncedProtocol, lastSavedProtocol, saveChanges])

  // Flush any unsaved changes on unmount
  useEffect(() => {
    return () => {
      if (
        !lastSavedProtocolRef.current ||
        !protocolRef.current ||
        deepEqual(lastSavedProtocolRef.current, protocolRef.current)
      ) {
        return
      }
      void saveChanges(protocolRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Check if there are unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    if (!protocol || !lastSavedProtocol) {
      return false
    }
    return !deepEqual(protocol, lastSavedProtocol)
  }, [protocol, lastSavedProtocol])

  // Block navigation when there are unsaved changes
  useBlocker({ when: hasUnsavedChanges })

  const handleSetAsOrgDefault = useCallback(async () => {
    if (!letterhead?.organization?.id || !letterhead?.id) {
      return
    }
    await updateRecordSettings({
      connection: { organizationId: letterhead.organization.id },
      settings: { defaultLetterheadId: letterhead.id },
    })
    await refetch()
  }, [letterhead?.id, letterhead?.organization?.id, refetch, updateRecordSettings])

  const handleDeleteConfirm = useCallback(async () => {
    if (!letterhead?.id) {
      return
    }
    await deleteLetterhead({ id: letterhead.id })
  }, [deleteLetterhead, letterhead?.id])

  const handleDeleteClosed = useCallback(() => {
    navigate(routes.letterheads())
  }, [])

  const menuItems = useMemo((): ActionItem[] => {
    const items: ActionItem[] = []

    if (!letterhead?.isDefaultForOrganization) {
      items.push({
        label: 'Make default for organization',
        onClick: handleSetAsOrgDefault,
        icon: 'FaCheck',
        key: 'set-default',
      })
    }

    items.push({
      label: 'Delete',
      onClick: deleteModalState.open,
      icon: 'FaTrash',
      key: 'delete',
    })

    return items
  }, [deleteModalState.open, handleSetAsOrgDefault, letterhead?.isDefaultForOrganization])

  if (isLoading) {
    return (
      <Page>
        <Page.Header title="Letterhead" backButtonHref={routes.letterheads()} />
        <Page.Content>
          <Skeleton active />
        </Page.Content>
      </Page>
    )
  }

  if (!letterhead) {
    return (
      <Page>
        <Page.Header title="Letterhead Not Found" backButtonHref={routes.letterheads()} />
        <Page.Content>
          <p>The requested letterhead could not be found.</p>
        </Page.Content>
      </Page>
    )
  }

  // Wait for form values to be initialized from letterhead data
  if (!formValues) {
    return (
      <Page>
        <Page.Header title="Letterhead" backButtonHref={routes.letterheads()} />
        <Page.Content>
          <Skeleton active />
        </Page.Content>
      </Page>
    )
  }

  const rightSidebar = (
    <div className="h-full overflow-y-auto border-l border-neutral-200 p-4">
      <LetterheadForm initialValues={formValues} onFormValuesChange={handleFormValuesChange} />
    </div>
  )

  return (
    <Page pageOverflowType="PageContentOnly" rightSidebar={rightSidebar}>
      <Page.Header
        title={letterhead.name || 'Untitled Letterhead'}
        subtitle={
          <VStack>
            <HStack>
              <T.BodySmall className="text-neutral-500">
                {isSaving ? <Icon.FaSpinner className="animate-spin" /> : <Icon.FaCloudCheck />}
                &nbsp;&nbsp;
                {lastSavedString}
              </T.BodySmall>
            </HStack>
          </VStack>
        }
        backButtonHref={routes.letterheads()}
        actions={<EllipsisButton items={menuItems} />}
      />
      <Page.Content className="flex items-center justify-center bg-neutral-50">
        <LetterheadPreview formValues={formValues} />
      </Page.Content>
      {deleteModalState.isOpen && (
        <DeleteLetterheadModal
          onConfirm={handleDeleteConfirm}
          onClosed={handleDeleteClosed}
          onClose={deleteModalState.close}
        />
      )}
    </Page>
  )
}

export default LetterheadPage
